'use strict';

import lazySizes from 'lazysizes';
import Component from "../../base/Component";

const SMOOTH_ANCHOR_SCROLL_CLASS = 'smooth-anchor-scroll';
// Fallback for browsers without `scrollend`, or when the target is already
// in place and no scroll (so no scrollend) happens. Also bounds how long late layout
// shifts (lazy images loading in) are followed, see onLayoutShift().
const SMOOTH_ANCHOR_SCROLL_MAX_MS = 3000;
// Stop following the target if the user scrolls themselves
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

/**
 * Smooth scrolling for same-page anchor links (e.g. linked cards pointing at a block's Section Identifier), offset
 * so the target lands below the fixed header. Same approach as amp-ui/rwi-ui: the browser does the scrolling, using
 * `scroll-padding-top` for the offset and a temporary `scroll-behavior: smooth` class for the animation (see
 * header.css), so opening a URL with a hash is offset too.
 *
 * The header (_partials/header.twig) is fixed, and Headroom (Headroom.js / header.css) slides its topbar out of view
 * while scrolling down and back in while scrolling up. So a jump down the page lands under the header minus the
 * topbar, and a jump up lands under the full header.
 *
 * Lazy images (lazysizes) have no height until they load, so on the first click any between the link and the target
 * load in during the scroll and push the target down after the browser has already picked where to stop. To handle
 * that, the images before the target are unveiled on click, and the target is scrolled to again whenever the page
 * resizes before the scroll is finished.
 */
export default class SmoothAnchor extends Component {
    constructor(props) {
        super(props)
        this.html = document.documentElement;
        this.header = document.querySelector('header');
        this.topbar = this.header ? this.header.querySelector('[data-header-topbar]') : null;
        this.timeout = undefined;
        this.target = null;
        this.done = this.done.bind(this);
        this.onScrollEnd = this.onScrollEnd.bind(this);
        this.resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => this.onLayoutShift())
            : null;
    }

    init() {
        if (!this.header) return;

        this.setHeaderHeights();
        window.addEventListener('resize', () => this.setHeaderHeights());
        // Capture phase so the class is set before any other click handler and the browser's default hash scroll
        document.addEventListener('click', (event) => this.onClickCapture(event), true);
    }

    setHeaderHeights() {
        const headerHeight = this.header.offsetHeight;
        const topbarHeight = this.topbar ? this.topbar.offsetHeight : 0;
        this.html.style.setProperty('--header-height', `${headerHeight}px`);
        this.html.style.setProperty('--header-scrolled-height', `${headerHeight - topbarHeight}px`);
    }

    onClickCapture(event) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const anchor = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
        if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank') return;

        const url = new URL(anchor.href, window.location.href);
        if (!url.hash || url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

        let target = null;
        try {
            target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        } catch (e) {
            target = null;
        }
        if (!target) return;

        this.done();
        this.setHeaderHeights();

        // Jumping up the page: Headroom will show the topbar, so offset by the full header height
        if (target.getBoundingClientRect().top < this.header.getBoundingClientRect().bottom) {
            this.html.style.setProperty('--anchor-scroll-offset', 'var(--header-height)');
        }

        this.target = target;
        this.unveilLazyImagesBefore(target);

        this.html.classList.add(SMOOTH_ANCHOR_SCROLL_CLASS);
        window.addEventListener('scrollend', this.onScrollEnd);
        USER_SCROLL_EVENTS.forEach((name) => window.addEventListener(name, this.done, { passive: true }));
        if (this.resizeObserver) this.resizeObserver.observe(document.body);
        this.timeout = setTimeout(this.done, SMOOTH_ANCHOR_SCROLL_MAX_MS);
    }

    // Start loading the lazy images the scroll will pass over now, rather than as they come into view mid-scroll
    unveilLazyImagesBefore(target) {
        document.querySelectorAll('.lazyload').forEach((el) => {
            if (el.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) {
                lazySizes.loader.unveil(el);
            }
        });
    }

    // The page grew/shrank (e.g. a lazy image loaded) while heading to the target, so head to its new position
    onLayoutShift() {
        if (!this.target) return;
        this.target.scrollIntoView({ block: 'start' });
    }

    onScrollEnd() {
        if (!this.target) return;

        const scrolledToBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
        const offset = parseFloat(getComputedStyle(this.html).scrollPaddingTop) || 0;
        const reachedTarget = Math.abs(this.target.getBoundingClientRect().top - offset) <= 1;

        // Landed short because the page grew after the scroll started: keep going (still smooth, the class is still on)
        if (!reachedTarget && !scrolledToBottom) {
            this.target.scrollIntoView({ block: 'start' });
            return;
        }

        this.done();
    }

    done() {
        clearTimeout(this.timeout);
        this.target = null;
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.html.classList.remove(SMOOTH_ANCHOR_SCROLL_CLASS);
        this.html.style.removeProperty('--anchor-scroll-offset');
        window.removeEventListener('scrollend', this.onScrollEnd);
        USER_SCROLL_EVENTS.forEach((name) => window.removeEventListener(name, this.done));
    }
}
